import React, { useEffect, useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Button, buttonVariants } from "@/components/ui/button";
import { ReloadIcon } from "@radix-ui/react-icons";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { useHistory } from "react-router-dom";
import { signIn, signOut, fetchAuthSession, getCurrentUser, confirmSignIn, rememberDevice, forgetDevice } from "aws-amplify/auth";
import { useAuth } from "../contexts/AuthContext";
import Home from "./Home";
import MFAChallenge from "../components/MFAChallenge";
import CognitoMFASetup from "../components/CognitoMFASetup";
import { getMFAStatus, recordLogin, setMFAPreference } from "../services/mfaService";
import { logFailedLogin } from "../services/loginHistoryService";

const schema = yup
  .object({
    email: yup
      .string()
      .email("Invalid email address")
      .required("Email address is required."),
    password: yup
      .string()
      .min(8, "Password must be at least 8 characters")
      .required("Password is required."),
  })
  .required();

const Login = () => {
  const history = useHistory();
  const { user, checkAuthStatus } = useAuth();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [showMFAChallenge, setShowMFAChallenge] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [totpSetupDetails, setTotpSetupDetails] = useState(null);
  const [isMFACompleting, setIsMFACompleting] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  const REMEMBER_ME_KEY = "trivistaRememberMe";

  const applyRememberMe = async () => {
    if (typeof window !== "undefined") {
      if (rememberMe) {
        const expiresAt = Date.now() + 30 * 24 * 60 * 60 * 1000;
        window.localStorage.setItem(
          REMEMBER_ME_KEY,
          JSON.stringify({ enabled: true, expiresAt })
        );
      } else {
        window.localStorage.removeItem(REMEMBER_ME_KEY);
      }
    }

    if (rememberMe) {
      try {
        await rememberDevice();
      } catch {
        // ignore device remember errors
      }
    } else {
      try {
        await forgetDevice();
      } catch {
        // ignore device forget errors
      }
    }
  };

  useEffect(() => {
    checkUserType();
  }, []);

  const checkUserType = async () => {
    try {
      // Try to get a valid session first
      const session = await fetchAuthSession();
      
      if (session.tokens?.accessToken) {
        // User has a valid session, check their groups
        const groups = session.tokens.accessToken.payload["cognito:groups"] || [];
        const userIsRegular = groups.includes("Users");
        const userIsAdmin = groups.includes("Admins");
        
        if (userIsRegular || userIsAdmin) {
          // User is properly authenticated with MFA, redirect to home
          history.push("/home");
          return;
        }
      }
      
      // If we get here, user needs to log in (possibly with MFA setup)
      setIsCheckingAuth(false);
    } catch (error) {
      // No valid session - user needs to log in
      setIsCheckingAuth(false);
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsCheckingAuth(false);
      // Update the AuthContext as well
      await checkAuthStatus();
      // Force a re-check of authentication status
      await checkUserType();
    } catch (error) {
      console.error("Error signing out:", error);
      // Even if there's an error, reset the state
      setIsCheckingAuth(false);
    }
  };

  const {
    register,
    setError,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      const email = data.email.toLowerCase();
      setUserEmail(email);

      if (!rememberMe && typeof window !== "undefined") {
        window.localStorage.removeItem(REMEMBER_ME_KEY);
      }

      const { isSignedIn, nextStep } = await signIn({
        username: email,
        password: data.password,
      });

      if (isSignedIn) {
        const session = await fetchAuthSession();
        const groups =
          session.tokens.accessToken.payload["cognito:groups"] || [];

        const userIsRegular = groups.includes("Users");
        const userIsAdmin = groups.includes("Admins");

        if (!userIsRegular && !userIsAdmin) {
          setError("root", {
            message: "Access denied. You don't have user privileges.",
          });
          await signOut();
          return;
        }

        await applyRememberMe();
        recordLogin(); // fire-and-forget
        await checkAuthStatus();
        history.push("/home");
      } else {
        if (nextStep.signInStep === "CONFIRM_SIGN_UP") {
          setError("root", {
            message: "Please confirm your email address before signing in.",
          });
        } else if (
          nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
        ) {
          setError("root", {
            message: "Please set a new password.",
          });
        } else if (
          nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_SOFTWARE_TOKEN_MFA_CODE"
        ) {
          setShowMFAChallenge(true);
          return;
        } else if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
          setShowMFAChallenge(true);
          return;
        } else if (nextStep.signInStep === "ASSOCIATE_SOFTWARE_TOKEN") {
          setMfaSetupRequired(true);
          setUserEmail(email);
          return;
        } else if (nextStep.signInStep === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP") {
          setMfaSetupRequired(true);
          setUserEmail(email);
          setTotpSetupDetails(nextStep.totpSetupDetails);
          return;
        } else {
          setMfaSetupRequired(true);
          setUserEmail(email);
          return;
        }
      }
    } catch (error) {
      logFailedLogin(data.email?.toLowerCase(), error); // fire-and-forget
      if (error.message?.includes("disabled")) {
        setError("root", {
          message:
            "Your account has been disabled. Please contact your administrator.",
        });
      } else if (error.message?.includes("already a signed in user")) {
        setError("root", {
          message:
            "You are already signed in. Please sign out first or refresh the page.",
        });
      } else {
        setError("root", {
          message: error.message || "An error occurred during sign-in.",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMFASubmit = async (mfaCode) => {
    try {
      setIsSubmitting(true);
      setError("root", { message: "" });

      const { isSignedIn } = await confirmSignIn({
        challengeResponse: mfaCode,
      });
      
      if (isSignedIn) {
        const session = await fetchAuthSession();
        const groups =
          session.tokens.accessToken.payload["cognito:groups"] || [];
        const userIsRegular = groups.includes("Users");
        const userIsAdmin = groups.includes("Admins");

        if (!userIsRegular && !userIsAdmin) {
          setError("root", {
            message: "Access denied. You don't have user privileges.",
          });
          await signOut();
          return;
        }

        await applyRememberMe();
        recordLogin(); // fire-and-forget
        await checkAuthStatus();
        history.push("/home");
      } else {
        setError("root", {
          message: "MFA verification failed. Please try again.",
        });
      }
    } catch (error) {
      console.error("MFA verification error:", error);
      logFailedLogin(userEmail, error); // fire-and-forget
      setError("root", {
        message: error.message || "MFA verification failed. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMFASuccess = async () => {
    try {
      const session = await fetchAuthSession();
      const groups =
        session.tokens.accessToken.payload["cognito:groups"] || [];
      const userIsRegular = groups.includes("Users");
      const userIsAdmin = groups.includes("Admins");

      if (!userIsRegular && !userIsAdmin) {
        setError("root", {
          message: "Access denied. You don't have user privileges.",
        });
        await signOut();
        return;
      }

      await applyRememberMe();
      setMFAPreference(); // fire-and-forget: sets Cognito UserMFASettingList + writes mfaSetupAt
      recordLogin();      // fire-and-forget: writes lastLoginAt
      await checkAuthStatus();
      history.push("/home");
    } catch (error) {
      console.error("Error after MFA success:", error);
      setError("root", {
        message: "An error occurred after MFA verification.",
      });
    }
  };

  const handleMFACancel = () => {
    setShowMFAChallenge(false);
    setUserEmail("");
  };

  // Show loading while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="relative min-h-screen bg-zinc-700 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }



  if (user) return <div />;

  // Show MFA challenge if required
  if (showMFAChallenge) {
    return (
      <div className="relative min-h-screen bg-zinc-700">
        <div
          className="fixed w-full h-full z-0 bg-cover bg-repeat bg-center"
          style={{ backgroundImage: `url(/bg-new.jpg)` }}
        />
        <div className="flex flex-col h-full items-center justify-center relative z-10">
          <div className="mt-4 md:mt-8 lg:mt-16 mb-4 md:mb-8 lg:mb-12">
            <img
              alt="logo"
              src="/TriVista_Horizontal_Logo_White.png"
              className="w-[300px]"
            />
          </div>
          <MFAChallenge
            onSubmit={handleMFASubmit}
            onCancel={handleMFACancel}
            loading={isSubmitting}
            error={errors.root?.message}
          />
        </div>
      </div>
    );
  }

  // Show MFA setup if required
  if (mfaSetupRequired) {
    return (
      <div className="relative min-h-screen bg-zinc-700">
        <div
          className="fixed w-full h-full z-0 bg-cover bg-repeat bg-center"
          style={{ backgroundImage: `url(/bg-new.jpg)` }}
        />
        <div className="flex flex-col h-full items-center justify-center relative z-10">
          <div className="mt-4 md:mt-8 lg:mt-16 mb-4 md:mb-8 lg:mb-12">
            <img
              alt="logo"
              src="/TriVista_Horizontal_Logo_White.png"
              className="w-[300px]"
            />
          </div>
          <CognitoMFASetup
            onComplete={async () => {
              setMfaSetupRequired(false);
              setUserEmail("");
              setTotpSetupDetails(null);
              
              try {
                const session = await fetchAuthSession();
                const groups = session.tokens.accessToken.payload["cognito:groups"] || [];
                const userIsRegular = groups.includes("Users");
                const userIsAdmin = groups.includes("Admins");

                if (!userIsRegular && !userIsAdmin) {
                  setError("root", {
                    message: "Access denied. You don't have user privileges.",
                  });
                  await signOut();
                  return;
                }

                await applyRememberMe();
                setMFAPreference(); // sets Cognito UserMFASettingList + writes mfaSetupAt
                recordLogin();      // writes lastLoginAt
                await checkAuthStatus();
                history.push("/home");
              } catch (error) {
                console.log("Error after MFA setup:", error);
                setError("root", {
                  message: "Error verifying user privileges after MFA setup.",
                });
                await signOut();
              }
            }}
            onCancel={() => {
              setMfaSetupRequired(false);
              setUserEmail("");
              setTotpSetupDetails(null);
            }}
            totpSetupDetails={totpSetupDetails}
            userEmail={userEmail}
          />
        </div>
      </div>
    );
  }

  // Show loading screen during MFA completion
  if (isMFACompleting) {
    return (
      <div className="relative min-h-screen bg-zinc-700">
        <div
          className="fixed w-full h-full z-0 bg-cover bg-repeat bg-center"
          style={{ backgroundImage: `url(/bg-new.jpg)` }}
        />
        <div className="flex flex-col h-full items-center justify-center relative z-10">
          <div className="mt-4 md:mt-8 lg:mt-16 mb-4 md:mb-8 lg:mb-12">
            <img
              alt="logo"
              src="/TriVista_Horizontal_Logo_White.png"
              className="w-[300px]"
            />
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
              <span className="text-white text-lg">Completing MFA setup...</span>
            </div>
            <p className="text-white text-sm">Redirecting to dashboard</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      {/* {authState?.isAuthenticated === true ? (
        <Home />
      ) : ( */}
      <div className="relative min-h-screen bg-zinc-700">
        <div
          className="fixed w-full h-full z-0 bg-cover bg-repeat bg-center"
          style={{ backgroundImage: `url(/bg-new.jpg)` }}
        />
        {/* <img src={'/bg-new.jpg'} className="fixed z-0 w-full h-full bg-cover bg-repeat blur-sm bg-center " /> */}

        <div className="flex flex-col h-full items-center justify-center relative z-10">
          <div className=" mt-4 md:mt-8 lg:mt-16 mb-4 md:mb-8 lg:mb-12">
            <img
              alt="logo"
              src="/TriVista_Horizontal_Logo_White.png"
              className="w-[300px]"
            />
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="max-w-2xl flex flex-col items-center justify-center mx-8 ">
              <h1 className="text-3xl text-white text-center lg:tracking-[0.75rem] tracking-[0.5rem]">
                TriVistaIQ
              </h1>
              <p className="mt-12 text-white text-xl text-center">
                Built on our collective experience as operators, TriVistaIQ is
                an analytics platform to provide manufacturers, distributors,
                and their private equity owners, actionable data and insights to
                drive sustained business performance.
              </p>
              <Card className="mt-4 w-full mx-8 md:mx-auto md:w-auto px-4">
                {errors.root && (
                  <Alert variant="destructive" className="mt-4 max-w-96">
                    <ExclamationTriangleIcon className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{errors.root.message}</AlertDescription>
                    {errors.root.message?.includes("already signed in") && (
                      <div className="mt-3 space-y-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleSignOut}
                          className="w-full"
                        >
                          Sign Out & Try Again
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={handleRefresh}
                          className="w-full"
                        >
                          Refresh Page
                        </Button>
                      </div>
                    )}
                  </Alert>
                )}
                <div className={`${errors.root ? "" : ""}  mt-4 `}>
                  <Label className=" text-xl">Username</Label>
                  <Input
                    className="h-8 w-80 sm:w-auto sm:min-w-80"
                    {...register("email")}
                  />
                  <div className="mt-1 flex gap-3 items-start">
                    {errors.email && (
                      <p className="text-red-500">{errors.email.message}</p>
                    )}
                  </div>
                </div>
                <div className="mt-4 ">
                  <Label className=" text-xl ">Password</Label>
                  <Input
                    className="h-8 w-80 sm:w-auto sm:min-w-80"
                    type="password"
                    {...register("password")}
                  />
                  <div className="mt-1 flex gap-3 items-right">
                    {errors.password && (
                      <p className="text-red-500">{errors.password.message}</p>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex items-center space-x-2">
                  <input
                    id="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="remember-me" className="text-sm">
                    Remember Me
                  </Label>
                </div>
                {/* ✅ Forgot Password Link */}
                <div className="mt-2 text-right">
                  <Link
                    to="/change-password"
                    className="text-sm text-blue-500 hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Button
                  className="my-4 w-full px-12 py-6 text-xl rounded-xl"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting && (
                    <ReloadIcon className="w-6 h-6 animate-spin mr-2" />
                  )}
                  {isSubmitting ? "Logging In..." : "Log In"}
                </Button>
              </Card>
            </div>
            
          </form>

          <div className="flex flex-col items-center gap-4 mt-4">
            <a
              href="https://trivista.com/"
              target="_self" // or "_blank" if you want to open in a new tab
              rel="noopener noreferrer"
              className={buttonVariants({
                variant: "default",
                className: "px-12 py-6 text-xl rounded-xl",
              })}
            >
              Back to TriVista.com
            </a>
          </div>
        </div>
      </div>
      {/* )} */}
    </div>
  );
};

export default Login;
